import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@repo/ui/components'
import { cn } from '@repo/ui/lib/utils'
import { useVirtualizer } from '@tanstack/react-virtual'
import { CheckIcon, ChevronsUpDownIcon, ListChecksIcon, ListIcon, X } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { JSX, memo, useCallback, useEffect, useRef, type UIEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { HStack, Surface, Text, VStack } from '../Atoms'
import { DEFAULT_MAX_HEIGHT, ROW_HEIGHT } from './constants'
import { useDropdownText, useDropdownUI, useDropdownValue } from './hooks'
import type {
  NewDropdownOption,
  NewDropdownProps,
  NewDropdownValue,
  NewDropdownValueArray,
} from './types'

function NewDropdownComponent<T = NewDropdownValue | NewDropdownValueArray>(
  props: NewDropdownProps<T>,
) {
  const { t } = useTranslation('common')
  const isSearchable = props.searchable ?? false

  // Use custom hooks
  const {
    currentValue,
    selectedOptions,
    selectedOptionsSet,
    isMultiSelect,
    handleClear,
    handleSelectAll,
    handleOptionSelect,
  } = useDropdownValue({
    value: props.value,
    onChange: props.onChange,
    defaultValue: props.defaultValue,
    selectionMode: props.selectionMode,
    allowUnselectInSingleMode: props.allowUnselectInSingleMode,
  })

  const {
    isOpen,
    setIsOpen,
    search,
    setSearch,
    showOnlySelected,
    animationKey,
    tempModeText,
    searchBoxRef,
    selectionHeaderRef,
    searchFooterRef,
    parentRef,
    handleDisplayModeToggle,
    calculateAvailableHeight,
    filteredOptions,
    shouldVirtualize: canVirtualize,
    resetShowOnlySelected,
  } = useDropdownUI({
    visibility: {
      searchable: isSearchable,
      hideSelectionHeader: props.hideSelectionHeader,
      hideSearchFooter: props.hideSearchFooter,
      searchTermSeparator: props.searchTermSeparator,
    },
    layout: {
      maxHeight: props.maxHeight,
      isMultiSelect,
      selectedOptionsLength: selectedOptions.length,
    },
    data: {
      options: props.options,
      selectedOptions,
    },
  })

  const shouldVirtualize = canVirtualize && !props.hasHorizontalScroll

  // Every path that changes the search text goes through here, so the caller hears about each.
  const { onSearchChange } = props
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value)
      onSearchChange?.(value)
    },
    [setSearch, onSearchChange],
  )

  // The text dies with this component — the filter sheet unmounts its dropdowns when it closes —
  // but the caller's mirror of it (what `useSearchedOptions` fetches by) lives on in the page. Tell
  // the caller, or the next mount opens with an empty box on the matches of a search nobody can
  // see any more. Read through a ref so the cleanup runs once, on unmount only.
  const onSearchChangeRef = useRef(onSearchChange)
  useEffect(() => {
    onSearchChangeRef.current = onSearchChange
  }, [onSearchChange])
  useEffect(() => () => onSearchChangeRef.current?.(''), [])

  const visibleOptions =
    props.maxVisibleOptions === undefined
      ? filteredOptions
      : filteredOptions.slice(0, props.maxVisibleOptions)
  const isTruncated = visibleOptions.length < filteredOptions.length

  // Server-paged lists: the next page is asked for when the user nears the end, and straight away
  // while the rows given do not even fill the list (the local filter can hide most of a page), so
  // the end stays reachable. `isSearching` guards the gap between a keystroke and the fresh first
  // page, when the old pages are still on screen.
  const { onEndReached, hasMoreOptions, isLoadingMore, isSearching } = props
  const isServerPaged = onEndReached !== undefined
  const canLoadMore = isServerPaged && !!hasMoreOptions && !isLoadingMore && !isSearching
  const handleListScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!canLoadMore) return
    const list = event.currentTarget
    if (list.scrollTop + list.clientHeight >= list.scrollHeight - ROW_HEIGHT * 2) onEndReached()
  }
  useEffect(() => {
    if (!isOpen || !canLoadMore) return
    const list = parentRef.current
    if (list && list.scrollHeight <= list.clientHeight) onEndReached()
  }, [isOpen, canLoadMore, onEndReached, parentRef, visibleOptions.length])

  const { triggerText, displayText, searchPlaceholder, emptyText } = useDropdownText({
    value: {
      currentValue: currentValue as T,
      isMultiSelect,
      selectedOptions,
    },
    textConfig: {
      text: props.text,
      placeholder: props.placeholder,
      tempModeText,
    },
    data: {
      options: props.options,
    },
  })

  // Memoize trigger button className
  const triggerClassName = cn(
    'w-full min-w-0 justify-between',
    props.style?.triggerClassName,
    currentValue &&
      currentValue !== '' &&
      (!isMultiSelect || (currentValue as NewDropdownValueArray).length > 0)
      ? 'text-foreground'
      : 'text-muted-foreground',
  )

  // Virtualization setup
  const rowVirtualizer = useVirtualizer({
    count: visibleOptions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    enabled: shouldVirtualize,
  })

  // Measure virtualizer when dropdown opens or filtered options change
  useEffect(() => {
    if (isOpen && shouldVirtualize) {
      rowVirtualizer.measure()
    }
  }, [isOpen, shouldVirtualize, rowVirtualizer, visibleOptions.length])

  // Memoize option rendering function
  const renderOption = useCallback(
    (option: NewDropdownOption) => {
      const isSelected = selectedOptionsSet.has(option.value)

      return (
        <CommandItem
          key={String(option.value)}
          value={String(option.value)}
          keywords={option.keywords}
          disabled={option.disabled}
          onSelect={() =>
            handleOptionSelect(option.value, setIsOpen, handleSearchChange, isSearchable)
          }
          className="group"
        >
          {isMultiSelect && (
            <CheckIcon
              className={cn(
                'mr-2 h-4 w-4 transition-opacity duration-300',
                isSelected ? 'opacity-100' : 'opacity-0',
              )}
            />
          )}
          <Text
            className={cn(
              'transition-colors duration-100',
              props.hasHorizontalScroll ? 'whitespace-nowrap' : 'w-full truncate',
              isSelected
                ? 'text-foreground'
                : 'text-foreground/70 group-data-[selected=true]:text-foreground',
              option.labelClassName,
            )}
          >
            {option.label}
          </Text>
          {!isMultiSelect && (
            <CheckIcon
              className={cn(
                'size-4 transition-opacity duration-300',
                isSelected ? 'opacity-100' : 'opacity-0',
              )}
            />
          )}
        </CommandItem>
      )
    },
    [
      isMultiSelect,
      selectedOptionsSet,
      handleOptionSelect,
      setIsOpen,
      handleSearchChange,
      isSearchable,
      props.hasHorizontalScroll,
    ],
  )

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <VStack gap="sm" className={cn('w-full', props.style?.stackClassName)}>
        {props.label && (
          <Label className={cn(props.disabled && 'text-muted-foreground')}>{props.label}</Label>
        )}

        <PopoverTrigger asChild>
          <Button
            variant="secondary"
            role="combobox"
            aria-expanded={isOpen}
            disabled={props.disabled || props.loading}
            className={triggerClassName}
          >
            <div className="flex min-w-0 items-center gap-2">
              {props.loading && (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              )}
              <span className="min-w-0 select-none truncate">{triggerText}</span>
            </div>
            <ChevronsUpDownIcon className="shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          align="start"
          className={cn('w-[--radix-popover-trigger-width] p-0', props.style?.popoverClassName)}
          onEscapeKeyDown={props.onEscapeKeyDown}
          onCloseAutoFocus={props.onCloseAutoFocus}
        >
          <Command
            className="rounded-md"
            style={{
              maxHeight: `${props.maxHeight || DEFAULT_MAX_HEIGHT}px`,
            }}
            shouldFilter={false}
          >
            {isSearchable && (
              <div className="relative" ref={searchBoxRef}>
                <CommandInput
                  placeholder={searchPlaceholder}
                  value={search}
                  onValueChange={handleSearchChange}
                />
                {search && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className="text-muted-foreground hover:text-foreground absolute right-1.5 top-1/2 h-6 w-6 -translate-y-1/2"
                    onClick={() => handleSearchChange('')}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            )}

            {/* Minimal input for keyboard navigation when not searchable */}
            {!isSearchable && (
              <div className="pointer-events-none h-0 opacity-0">
                <CommandInput placeholder="" value="" onValueChange={() => {}} />
              </div>
            )}

            {!props.hideSelectionHeader && isMultiSelect && (
              <Surface
                border={false}
                ref={selectionHeaderRef}
                className={cn(
                  'flex w-full min-w-0 shrink-0 items-center justify-between overflow-hidden border-b px-1 transition-all duration-200',
                  selectedOptions.length > 0 || props.selectAllEnabled
                    ? 'max-h-12 py-1 opacity-100'
                    : 'max-h-0 border-b-0 py-0 opacity-0',
                )}
              >
                <HStack gap="xs" align="center" className="min-w-0 flex-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className="text-muted-foreground hover:text-foreground flex-none transition-colors duration-200"
                    onClick={handleDisplayModeToggle}
                  >
                    {showOnlySelected ? <ListChecksIcon /> : <ListIcon />}
                  </Button>

                  <AnimatePresence mode="wait">
                    <motion.div
                      key={animationKey}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                      className="flex-1 select-none truncate"
                    >
                      <Text size="xs" weight="medium" tone="muted">
                        {selectedOptions.length > 0
                          ? displayText
                          : t('newDropdown.noElementSelected')}
                      </Text>
                    </motion.div>
                  </AnimatePresence>
                </HStack>

                {selectedOptions.length > 0 ? (
                  <Button
                    type="button"
                    className="text-muted-foreground hover:text-foreground flex-none transition-colors duration-200"
                    variant="link"
                    size="sm"
                    onClick={() => handleClear(resetShowOnlySelected)}
                  >
                    {t('newDropdown.unselectAll')}
                  </Button>
                ) : props.selectAllEnabled ? (
                  <Button
                    type="button"
                    className="text-muted-foreground hover:text-foreground flex-none transition-colors duration-200"
                    variant="link"
                    size="sm"
                    onClick={() =>
                      handleSelectAll(
                        props.options.filter((opt) => !opt.disabled).map((opt) => opt.value),
                        resetShowOnlySelected,
                      )
                    }
                  >
                    {t('newDropdown.selectAll')}
                  </Button>
                ) : null}
              </Surface>
            )}

            <CommandList
              className="relative overflow-auto transition-all duration-200 ease-in-out"
              ref={parentRef}
              style={{
                maxHeight: calculateAvailableHeight(),
              }}
              onScroll={handleListScroll}
            >
              {props.loading && <CommandLoading>{t('loading')}</CommandLoading>}
              {!props.loading && filteredOptions.length === 0 && (
                <>
                  {props.isSearching ? (
                    <CommandLoading>{t('loading')}</CommandLoading>
                  ) : (
                    <CommandEmpty>{emptyText}</CommandEmpty>
                  )}
                </>
              )}
              <CommandGroup
                className={
                  props.hasHorizontalScroll ? 'w-max min-w-full overflow-visible' : undefined
                }
              >
                {shouldVirtualize ? (
                  <div
                    style={{
                      height: `${rowVirtualizer.getTotalSize()}px`,
                      position: 'relative',
                    }}
                  >
                    {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                      const option = visibleOptions[virtualRow.index]
                      return (
                        <div
                          key={String(option.value)}
                          className="absolute left-0 w-full"
                          style={{
                            top: virtualRow.start,
                          }}
                        >
                          {renderOption(option)}
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  visibleOptions.map((option) => renderOption(option))
                )}
              </CommandGroup>
              {props.isLoadingMore && <CommandLoading>{t('loading')}</CommandLoading>}
            </CommandList>

            {!props.hideSearchFooter && (search || isTruncated || hasMoreOptions) && (
              <Surface
                border={false}
                tone="secondary"
                ref={searchFooterRef}
                className="flex justify-end border-t px-1.5 py-0.5"
              >
                <Text tone="muted" className="text-[11px]">
                  {isServerPaged ? (
                    // The options given are the pages loaded so far, not every match: the server
                    // holds the rest, so "x of y" has no y. Say how many are shown and, while more
                    // exist, that scrolling brings them.
                    hasMoreOptions ? (
                      t('newDropdown.moreOnScroll', { count: filteredOptions.length })
                    ) : (
                      t('newDropdown.results', { count: filteredOptions.length })
                    )
                  ) : (
                    <>
                      {/* Truncated: how many of the matches are shown. Otherwise: matches of all options. */}
                      {isTruncated
                        ? t('newDropdown.of', { count: visibleOptions.length })
                        : search && t('newDropdown.of', { count: filteredOptions.length })}
                      {t('newDropdown.results', {
                        count: isTruncated ? filteredOptions.length : props.options.length,
                      })}
                    </>
                  )}
                </Text>
              </Surface>
            )}
          </Command>
        </PopoverContent>
      </VStack>
    </Popover>
  )
}

export const NewDropdown = memo(NewDropdownComponent) as <
  T = NewDropdownValue | NewDropdownValueArray,
>(
  props: NewDropdownProps<T>,
) => JSX.Element
